import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Edit, Trash2, Upload, Search, Filter, RefreshCcw } from 'lucide-react';
import { AccountCombobox } from '../components/AccountCombobox';

const EMPTY_FORM = {
  kind: 'main',          // رئيسي | فرعي
  account_name_ar: '',
  account_name_en: '',
  parent_code: '',
  account_type: 'asset',
  nature: 'debit',
  closing_type: 'balance_sheet',
  account_code: '',
  level: 1,
  is_active: true,
  sub_code_digits: 2,
  description: ''
};

const SUB_CODE_SCHEMES = [
  { value: 1, label: 'خانة واحدة - حد أقصى 9 حسابات ثانوية' },
  { value: 2, label: '01 - حد أقصى 99 حساب ثانوي' },
  { value: 3, label: '02 - حد أقصى 999 حساب ثانوي' },
  { value: 4, label: '03 - حد أقصى 9999 حساب ثانوي' },
];

// أصول/مصروفات = مدين بطبيعتها، خصوم/حقوق ملكية/إيرادات = دائنة بطبيعتها (عرف محاسبي قياسي)
const deriveNature = (accountType) => (['asset', 'expense'].includes(accountType) ? 'debit' : 'credit');
const deriveClosingType = (accountType) => (['revenue', 'expense'].includes(accountType) ? 'income_statement' : 'balance_sheet');

// ==================== مكوّن صف الحقل (تصميم مطابق للنموذج المطلوب) ====================
const FieldRow = ({ label, required, children, muted }) => (
  <div className={`flex items-stretch border rounded-md overflow-hidden ${muted ? 'bg-gray-50' : 'bg-white'}`}>
    <div className="w-40 shrink-0 flex items-center justify-end px-3 py-2 bg-gray-50 border-s text-sm text-gray-600 font-medium">
      {label}{required && <span className="text-red-500 ms-0.5">*</span>}
    </div>
    <div className="flex-1 min-w-0">{children}</div>
  </div>
);

export default function ChartOfAccounts() {
  const { i18n } = useTranslation();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [accountStats, setAccountStats] = useState(null);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    loadAccounts();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally run once on mount
  }, []);

  const loadAccounts = async () => {
    try {
      const response = await api.get('/accounts');
      setAccounts(response.data);
      setForm((prev) => {
        if (prev.account_code || editMode) return prev;
        const topLevel = response.data.filter((a) => !a.parent_code);
        const codes = topLevel.map((a) => parseInt(a.account_code, 10)).filter((n) => !isNaN(n));
        const next = codes.length > 0 ? Math.max(...codes) + 1 : 1;
        return { ...prev, account_code: next <= 9 ? String(next) : '' };
      });
    } catch (error) {
      toast.error('حدث خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/accounts/stats');
      setAccountStats(response.data);
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const handleResetToDefault = async () => {
    setResetting(true);
    try {
      const response = await api.post('/accounts/reset-to-default');
      toast.success(`${response.data.message} (${response.data.count} حساب)`);
      setResetConfirmOpen(false);
      loadAccounts();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ أثناء إعادة التعيين');
    } finally {
      setResetting(false);
    }
  };

  const handleImportExcel = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await api.post('/accounts/import-excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`تم استيراد ${response.data.imported} حساب بنجاح!`);
      loadAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل استيراد الملف');
    } finally {
      event.target.value = '';
    }
  };

  // ==================== منطق النموذج ====================

  const parentOptions = useMemo(
    () => accounts.filter((a) => !editMode || a.account_code !== form.account_code),
    [accounts, editMode, form.account_code]
  );

  const suggestTopLevelCode = () => {
    const topLevel = accounts.filter((a) => !a.parent_code);
    const codes = topLevel.map((a) => parseInt(a.account_code, 10)).filter((n) => !isNaN(n));
    const next = codes.length > 0 ? Math.max(...codes) + 1 : 1;
    if (next > 9) {
      toast.error('تم الوصول للحد الأقصى (9) للحسابات الرئيسية الأساسية');
      return '';
    }
    return String(next);
  };

  const suggestNextCode = (parentCode) => {
    const parent = accounts.find((a) => a.account_code === parentCode);
    const digits = parent?.sub_code_digits || 2;
    const maxChildren = Math.pow(10, digits) - 1;

    const children = accounts.filter((a) => a.parent_code === parentCode);
    const suffixes = children
      .map((c) => c.account_code.slice(parentCode.length))
      .filter((s) => /^\d+$/.test(s) && s.length === digits)
      .map((s) => parseInt(s, 10));

    const nextSuffix = suffixes.length > 0 ? Math.max(...suffixes) + 1 : 1;
    if (nextSuffix > maxChildren) {
      toast.error(`تم الوصول للحد الأقصى (${maxChildren}) للحسابات الفرعية تحت هذا الحساب حسب نظام الترقيم المحدد له`);
      return '';
    }
    return `${parentCode}${String(nextSuffix).padStart(digits, '0')}`;
  };

  const handleKindChange = (kind) => {
    if (kind === 'main') {
      setForm((prev) => ({ ...prev, kind, parent_code: '', level: 1, account_code: suggestTopLevelCode() }));
    } else {
      setForm((prev) => ({ ...prev, kind }));
    }
  };

  const handleParentChange = (parentCode) => {
    const parent = accounts.find((a) => a.account_code === parentCode);
    setForm((prev) => ({
      ...prev,
      parent_code: parentCode,
      level: parent ? parent.level + 1 : prev.level,
      account_type: parent?.account_type || prev.account_type,
      nature: parent?.nature || deriveNature(parent?.account_type || prev.account_type),
      closing_type: parent?.closing_type || deriveClosingType(parent?.account_type || prev.account_type),
      account_code: editMode ? prev.account_code : suggestNextCode(parentCode),
    }));
  };

  const handleAccountTypeChange = (accountType) => {
    setForm((prev) => ({
      ...prev,
      account_type: accountType,
      nature: deriveNature(accountType),
      closing_type: deriveClosingType(accountType),
    }));
  };

  const resetForm = () => {
    setForm({ ...EMPTY_FORM, account_code: suggestTopLevelCode() });
    setEditMode(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.account_name_ar || !form.account_code) {
      toast.error('يرجى تعبئة عنوان الحساب وكود الحساب');
      return;
    }
    if (form.kind === 'sub' && !form.parent_code) {
      toast.error('يرجى اختيار الحساب الرئيسي للحساب الفرعي');
      return;
    }

    const payload = {
      account_code: form.account_code,
      account_name_ar: form.account_name_ar,
      account_name_en: form.account_name_en || form.account_name_ar,
      account_type: form.account_type,
      nature: form.nature,
      closing_type: form.closing_type,
      description: form.description,
      parent_code: form.kind === 'sub' ? form.parent_code : null,
      level: form.level,
      is_active: form.is_active,
      sub_code_digits: form.sub_code_digits,
    };

    try {
      if (editMode) {
        await api.put(`/accounts/${form.account_code}`, payload);
        toast.success('تم تحديث الحساب بنجاح');
      } else {
        await api.post('/accounts', payload);
        toast.success('تمت إضافة الحساب بنجاح');
      }
      resetForm();
      loadAccounts();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ أثناء الحفظ');
    }
  };

  const openEditForm = (account) => {
    setForm({
      kind: account.parent_code ? 'sub' : 'main',
      account_name_ar: account.account_name_ar,
      account_name_en: account.account_name_en,
      parent_code: account.parent_code || '',
      account_type: account.account_type,
      nature: account.nature || deriveNature(account.account_type),
      closing_type: account.closing_type || deriveClosingType(account.account_type),
      account_code: account.account_code,
      level: account.level,
      is_active: account.is_active,
      sub_code_digits: account.sub_code_digits || 2,
      description: account.description || ''
    });
    setEditMode(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (code) => {
    if (window.confirm('هل أنت متأكد من حذف هذا الحساب؟')) {
      try {
        await api.delete(`/accounts/${code}`);
        toast.success('تم الحذف بنجاح');
        loadAccounts();
        loadStats();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'حدث خطأ');
      }
    }
  };

  // ==================== عرض القائمة ====================

  const getTypeColor = (type) => ({
    asset: 'bg-blue-100 text-blue-700 border-blue-200',
    liability: 'bg-red-100 text-red-700 border-red-200',
    equity: 'bg-purple-100 text-purple-700 border-purple-200',
    revenue: 'bg-green-100 text-green-700 border-green-200',
    expense: 'bg-orange-100 text-orange-700 border-orange-200',
  }[type] || 'bg-gray-100 text-gray-700 border-gray-200');

  const getTypeLabel = (type) => ({
    asset: 'أصول', liability: 'خصوم', equity: 'حقوق ملكية', revenue: 'إيرادات', expense: 'مصروفات'
  }[type] || type);

  const filteredAccounts = useMemo(() => {
    return accounts.filter((account) => {
      const matchesSearch = !searchTerm ||
        account.account_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.account_name_ar.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (account.account_name_en || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = filterType === 'all' || account.account_type === filterType;
      return matchesSearch && matchesType;
    });
  }, [accounts, searchTerm, filterType]);

  const totalPages = Math.max(1, Math.ceil(filteredAccounts.length / pageSize));
  
  const pagedAccounts = useMemo(() => {
    return filteredAccounts.slice((page - 1) * pageSize, page * pageSize);
  }, [filteredAccounts, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, filterType, pageSize]);

  // خريطة بحث سريعة للحسابات بالكود
  const accountsByCode = useMemo(() => {
    const map = new Map();
    accounts.forEach((a) => map.set(a.account_code, a));
    return map;
  }, [accounts]);

  const getParentLabel = (parentCode) => {
    if (!parentCode) return '—';
    const parent = accountsByCode.get(parentCode);
    return parent ? `${parent.account_code} - ${parent.account_name_ar}` : parentCode;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-muted-foreground">جاري التحميل...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4" dir="rtl" data-testid="chart-of-accounts-container">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">إدارة الدليل المحاسبي</h1>
          <p className="text-sm text-muted-foreground">{accounts.length} حساب</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleImportExcel} style={{ display: 'none' }} />

          <Dialog open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="text-orange-600 border-orange-300 hover:bg-orange-50" data-testid="reset-accounts-btn">
                <RefreshCcw className="h-4 w-4 me-1" />
                إعادة تعيين ({accountStats?.default_available || 2145})
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md" dir="rtl">
              <DialogHeader>
                <DialogTitle className="text-center text-lg">إعادة تعيين دليل الحسابات</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-amber-800 text-sm font-medium mb-2">تحذير!</p>
                  <p className="text-amber-700 text-sm">
                    سيتم حذف جميع الحسابات الحالية ({accounts.length} حساب) واستبدالها بالدليل الافتراضي ({accountStats?.default_available || 2145} حساب).
                  </p>
                </div>
                <div className="flex gap-2 justify-end pt-2">
                  <Button variant="outline" onClick={() => setResetConfirmOpen(false)}>إلغاء</Button>
                  <Button className="bg-orange-600 hover:bg-orange-700" onClick={handleResetToDefault} disabled={resetting} data-testid="confirm-reset-btn">
                    {resetting ? 'جاري إعادة التعيين...' : 'تأكيد إعادة التعيين'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
            <Upload className="h-4 w-4 me-1" />
            استيراد
          </Button>
        </div>
      </div>

      {/* ==================== نموذج إضافة/تعديل حساب ==================== */}
      <Card className="p-5">
        <h2 className="text-lg font-semibold mb-4">{editMode ? 'تعديل حساب' : 'إضافة حساب جديد'}</h2>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldRow label="نوع الحساب" required>
              <Select value={form.kind} onValueChange={handleKindChange}>
                <SelectTrigger className="border-0 rounded-none focus:ring-0"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="main">رئيسي</SelectItem>
                  <SelectItem value="sub">فرعي</SelectItem>
                </SelectContent>
              </Select>
            </FieldRow>

            <FieldRow label="عنوان الحساب" required>
              <input
                value={form.account_name_ar}
                onChange={(e) => setForm({ ...form, account_name_ar: e.target.value })}
                placeholder="مثال: تبرعات وهبات مقيدة"
                className="w-full h-full px-3 py-2 text-sm outline-none bg-transparent"
                data-testid="account-name-input"
              />
            </FieldRow>

            <FieldRow label="حساب رئيسي" required={form.kind === 'sub'} muted={form.kind === 'main'}>
              {form.kind === 'main' ? (
                <div className="px-3 py-2 text-sm text-gray-400">لا ينطبق - حساب رئيسي</div>
              ) : (
                <AccountCombobox
                  accounts={parentOptions}
                  value={form.parent_code || ''}
                  onSelect={handleParentChange}
                  placeholder="اختر الحساب الرئيسي (اكتب للبحث)"
                  className="border-0 rounded-none focus:ring-0 h-full"
                />
              )}
            </FieldRow>

            <FieldRow label="طبيعة الحساب" required>
              <Select value={form.nature} onValueChange={(v) => setForm({ ...form, nature: v })}>
                <SelectTrigger className="border-0 rounded-none focus:ring-0"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="debit">مدين</SelectItem>
                  <SelectItem value="credit">دائن</SelectItem>
                </SelectContent>
              </Select>
            </FieldRow>

            <FieldRow label="إقفال" required>
              <Select value={form.closing_type} onValueChange={(v) => setForm({ ...form, closing_type: v })}>
                <SelectTrigger className="border-0 rounded-none focus:ring-0"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="balance_sheet">قائمة المركز المالي</SelectItem>
                  <SelectItem value="income_statement">قائمة إيرادات و مصروفات</SelectItem>
                </SelectContent>
              </Select>
            </FieldRow>

            <FieldRow label="التصنيف" required>
              <Select value={form.account_type} onValueChange={handleAccountTypeChange}>
                <SelectTrigger className="border-0 rounded-none focus:ring-0"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="asset">أصول</SelectItem>
                  <SelectItem value="liability">خصوم</SelectItem>
                  <SelectItem value="equity">حقوق ملكية</SelectItem>
                  <SelectItem value="revenue">إيرادات</SelectItem>
                  <SelectItem value="expense">مصروفات</SelectItem>
                </SelectContent>
              </Select>
            </FieldRow>

            <FieldRow label="التسلسل الثانوي" required>
              <Select
                value={String(form.sub_code_digits)}
                onValueChange={(v) => setForm({ ...form, sub_code_digits: Number(v) })}
              >
                <SelectTrigger className="border-0 rounded-none focus:ring-0"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SUB_CODE_SCHEMES.map((s) => (
                    <SelectItem key={s.value} value={String(s.value)}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldRow>

            <FieldRow label="كود الحساب" required muted>
              <input
                value={form.account_code}
                readOnly
                disabled
                placeholder="يُنشأ تلقائيًا حسب الحساب الرئيسي ونظام الترقيم"
                className="w-full h-full px-3 py-2 text-sm outline-none bg-transparent font-mono text-gray-500 cursor-not-allowed"
                data-testid="account-code-input"
                title="يُنشأ الكود تلقائيًا بناءً على الحساب الرئيسي والدرجة ونظام الترقيم المحدد، ولا يمكن تعديله يدويًا لضمان تناسق الشجرة"
              />
            </FieldRow>

            <FieldRow label="المستوى" muted>
              <div className="px-3 py-2 text-sm text-gray-600">{form.level}</div>
            </FieldRow>
          </div>

          <FieldRow label="الاسم الإنجليزي">
            <input
              value={form.account_name_en}
              onChange={(e) => setForm({ ...form, account_name_en: e.target.value })}
              placeholder="Account Name (optional)"
              className="w-full h-full px-3 py-2 text-sm outline-none bg-transparent"
            />
          </FieldRow>

          <div className="flex items-stretch border rounded-md overflow-hidden">
            <div className="w-40 shrink-0 flex items-center justify-end px-3 py-2 bg-gray-50 border-s text-sm text-gray-600 font-medium">
              الوصف
            </div>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="وصف الحساب (اختياري)"
              className="border-0 rounded-none focus-visible:ring-0 min-h-[60px]"
            />
          </div>

          <div className="flex items-center justify-between border rounded-md px-3 py-2">
            <span className="text-sm text-gray-600 font-medium">مغلق:</span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{!form.is_active ? 'نعم' : 'لا'}</span>
              <Switch checked={!form.is_active} onCheckedChange={(checked) => setForm({ ...form, is_active: !checked })} />
            </div>
          </div>

          <div className="flex justify-center gap-2 pt-2">
            <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 px-10" data-testid="save-account-btn">
              {editMode ? 'حفظ التعديلات' : 'إضافة السجل'}
            </Button>
            {editMode && (
              <Button type="button" variant="outline" onClick={resetForm}>إلغاء التعديل</Button>
            )}
          </div>
        </form>
      </Card>

      {/* ==================== شريط البحث والفلاتر ==================== */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="بحث بالرمز أو الاسم..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="ps-9"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40">
              <Filter className="h-4 w-4 me-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">الكل</SelectItem>
              <SelectItem value="asset">أصول</SelectItem>
              <SelectItem value="liability">خصوم</SelectItem>
              <SelectItem value="equity">حقوق ملكية</SelectItem>
              <SelectItem value="revenue">إيرادات</SelectItem>
              <SelectItem value="expense">مصروفات</SelectItem>
            </SelectContent>
          </Select>
          <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="20">20 بالصفحة</SelectItem>
              <SelectItem value="50">50 بالصفحة</SelectItem>
              <SelectItem value="100">100 بالصفحة</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* ==================== الجدول ==================== */}
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b text-gray-600">
                <th className="text-center py-2 px-3 font-medium">إجراءات</th>
                <th className="text-start py-2 px-3 font-medium">تاريخ الإنشاء</th>
                <th className="text-start py-2 px-3 font-medium">إقفال</th>
                <th className="text-start py-2 px-3 font-medium">الطبيعة</th>
                <th className="text-start py-2 px-3 font-medium">التصنيف</th>
                <th className="text-start py-2 px-3 font-medium">نوع الحساب</th>
                <th className="text-start py-2 px-3 font-medium">المستوى</th>
                <th className="text-start py-2 px-3 font-medium">حساب رئيسي</th>
                <th className="text-start py-2 px-3 font-medium">عنوان الحساب</th>
                <th className="text-start py-2 px-3 font-medium">كود الحساب</th>
                <th className="text-start py-2 px-3 font-medium">حالة الحساب</th>
              </tr>
            </thead>
            <tbody>
              {pagedAccounts.length === 0 ? (
                <tr>
                  <td colSpan={11} className="text-center py-12 text-muted-foreground">
                    {searchTerm ? 'لا توجد نتائج للبحث' : 'لا توجد حسابات'}
                  </td>
                </tr>
              ) : (
                pagedAccounts.map((account) => (
                  <tr key={account.account_code} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-3">
                      <div className="flex items-center justify-center gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEditForm(account)}>
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleDelete(account.account_code)}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-gray-500 whitespace-nowrap">
                      {account.created_at ? new Date(account.created_at).toLocaleDateString('ar-SA') : '—'}
                    </td>
                    <td className="py-2 px-3 whitespace-nowrap">
                      {account.closing_type === 'income_statement' ? 'قائمة إيرادات ومصروفات' : 'قائمة المركز المالي'}
                    </td>
                    <td className="py-2 px-3">{account.nature === 'credit' ? 'دائن' : 'مدين'}</td>
                    <td className="py-2 px-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${getTypeColor(account.account_type)}`}>
                        {getTypeLabel(account.account_type)}
                      </span>
                    </td>
                    <td className="py-2 px-3">{account.parent_code ? 'فرعي' : 'رئيسي'}</td>
                    <td className="py-2 px-3">{account.level}</td>
                    <td className="py-2 px-3 text-gray-500 whitespace-nowrap max-w-[200px] truncate">{getParentLabel(account.parent_code)}</td>
                    <td className="py-2 px-3 font-medium">
                      {i18n.language === 'ar' ? account.account_name_ar : (account.account_name_en || account.account_name_ar)}
                    </td>
                    <td className="py-2 px-3 font-mono text-gray-500">{account.account_code}</td>
                    <td className="py-2 px-3">
                      <Badge className={account.is_active ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-gray-200 text-gray-600 hover:bg-gray-200'}>
                        {account.is_active ? 'مفعل' : 'معطل'}
                      </Badge>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {filteredAccounts.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t text-sm text-gray-500">
            <span>
              عرض {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, filteredAccounts.length)} من {filteredAccounts.length}
            </span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>السابق</Button>
              <span>{page} / {totalPages}</span>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>التالي</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
